import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';

const PageContainer = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.div`
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)),
              url('/images/contact-hero.jpg') center/cover no-repeat;
  padding: 100px 2rem;
  color: white;
  text-align: center;
`;

const MainHeading = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-family: 'Playfair Display', serif;
  margin-bottom: 1.5rem;
`;

const Section = styled.section`
  padding: 5rem 2rem;
  background-color: ${props => props.background || 'white'};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 10px;
  height: fit-content;
`;

const InfoItem = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const InfoContent = styled.p`
  color: #666;
  line-height: 1.6;
  margin: 0.5rem 0;
`;

const ContactForm = styled.form`
  padding: 2rem;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const SubmitButton = styled.button`
  background-color: #4CAF50;
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 5px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #45a049;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // EmailJS configuration from environment variables
  const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

  // Initialize EmailJS once with the public key
  useEffect(() => {
    if (publicKey) {
      emailjs.init({ publicKey });
    }
  }, [publicKey]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Ensure EmailJS configuration is present
      if (!serviceId || !templateId || !publicKey) {
        alert('Email service is not configured. Please try again later.');
        return;
      }

      // Send email via EmailJS
      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          message: formData.message,
        },
        { publicKey }
      );

      alert('Thank you for your message. We will get back to you soon!');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      alert('There was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <HeroSection>
        <MainHeading>Contact Us</MainHeading>
        <p>Get in touch with our team</p>
      </HeroSection>
      
      <Section>
        <Container>
          <ContactGrid>
            <ContactInfo>
              <InfoItem>
                <InfoTitle>Address</InfoTitle>
                <InfoContent>1&2 Police Academy,</InfoContent>
                <InfoContent>Paynesville Monrovia, Liberia</InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoTitle>Phone</InfoTitle>
                <InfoContent>+231-887290859</InfoContent>
                <InfoContent>+231773986529</InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoTitle>Email</InfoTitle>
                <InfoContent>sunbold26@gmail.com</InfoContent>
              </InfoItem>
            </ContactInfo>
            
            <ContactForm onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="message">Message</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </SubmitButton>
            </ContactForm>
          </ContactGrid>
        </Container>
      </Section>
    </PageContainer>
  );
};

export default ContactPage;
